import React, { useState, useEffect, useCallback } from 'react';
import LitigationDeadline from '../dataObjects/LitigationDeadline';

import FilterLitigationDeadlines from './FilterLitigationDeadlines';
import EditOrAddNewLitigationDeadline from './EditOrAddNewLitigationDeadline';
import LitigationDeadlineTable from './LitigationDeadlineTable';
import FilterCriteria from '../dataObjects/FilterCriteria';
import UnfiledReport from '../dataObjects/UnfliledReport';
import EditOrAddNewUnfiledReport from './EditOrAddNewUnfiledReport';

import { Button, Box, Link } from '@mui/material/';
import { CloudDownload } from '@mui/icons-material';
import '../view-nonfiler-tracker-styles.css';


function LitigationDeadlines() {

    const [litigationDeadlinesList, setLitigationDeadlinesList] = useState<LitigationDeadline[] >([]);
    const [currentUnfiledReport, setCurrentUnfiledReport] = useState<UnfiledReport | null>(null);
    const [openUnfiledReportDialog, setOpenUnfiledReportDialog] = useState(false);
    const [openReportDialog, setOpenReportDialog] = useState(false);

    const [openDialog, setOpenDialog] = useState(false);
    const [currentRecord, setCurrentRecord] = useState<LitigationDeadline | null>(null);
    const [isEditing, setIsEditing] = useState(false);


    const [sortField, setSortField] = useState('');
    const [sortDirection, setSortDirection] = useState('');
    const [filterCriteria, setFilterCriteria] = useState<FilterCriteria>({
        year: "",
        reportFiled: false,
        isArchived: false,
        isLocal: false
    });

    const [errors, setErrors] = useState({
        name: { error: false, helperText: "" },
        cpfId: { error: false, helperText: "" },
        // Add more fields as necessary...
    });


    const getLitigationDeadlines = useCallback(async () => {
        try {
            const params = new URLSearchParams({
                SortField: sortField,
                SortDirection: sortDirection,
                ReportYear: filterCriteria.year,
                ReportFiled: filterCriteria.reportFiled.toString(),
                IsArchived: filterCriteria.isArchived.toString(),
                isLocal: filterCriteria.isLocal.toString()
            });





            const url = `/NonfilerTracker/GetLitigationDeadlineRecords?${params}`;
            const response = await fetch(url);
            const litigationData: { result: LitigationDeadline[] } = await response.json();

            // Verify that litigationData.result is an array and not empty
            if (!Array.isArray(litigationData.result) || litigationData.result.length === 0) {
                console.warn("No litigation data available or the array is empty.");
                setLitigationDeadlinesList([]);  // Set empty array in case there's no data
                return;
            }

            const litigationDeadlineWithReports = await Promise.all(litigationData.result.map(async (litigationDeadline: LitigationDeadline) => {
                const reportsUrl = `/NonfilerTracker/GetFilersUnfiledReports?localFilerRecordId=${litigationDeadline.litigationDeadlineRecordId}`;
                const reportsResponse = await fetch(reportsUrl);
                const reportsData: { result: UnfiledReport[] } = await reportsResponse.json();

                return {
                    ...litigationDeadline,
                    unfiledReports: reportsData.result
                };
            }));


            // Set state to litigationData.result (an array of LitigationDeadline objects)
            setLitigationDeadlinesList(litigationDeadlineWithReports);


        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, [sortField, sortDirection, filterCriteria]);

    useEffect(() => {
        getLitigationDeadlines();
    }, [getLitigationDeadlines]);

    const handleSort = (field: string) => {
        setSortField(field);
        setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    };

    const handleFilter = (criteria: Partial<FilterCriteria>) => {
        setFilterCriteria(prev => ({ ...prev, ...criteria }));
    };

    const handleOpenDialog = (record: LitigationDeadline | null = null, editing: boolean = false) => {
        setCurrentRecord(record ||
        {
            litigationDeadlineRecordId: 0,
            name: '',
            cpfId: 0,
            referredToAGODateString: '',
            agoLetterSentDateString: '',
            deadlineForResponseAGOString: '',
            mailedToSheriffDateString: '',
            complaintFiledDateString: '',
            servedDateString: '',
            returnedBySheriffDateString: '',
            proofOfServiceDateString: '',
            notifiedSOOrClerkDateString: '',
            trackingOrderMailedToDDateString: '',
            trackingOrderMailedToCourtDateString: '',
            answerDueDateString: '',
            answerFiledDateString: '',
            mtnEnterDefaultFiledDateString: '',
            hearingDateString: '',
            defaultEnteredDateString: '',
            reportFiledDateString: '',
            notifiedSOOrClerkReportDateString: '',
            resolutionStatus: 0,
            isArchived: false,
            reportType: 0,
            reportYearDateString: '',
            isLocal: false,
            unfiledReports: []
        });


        setIsEditing(editing);
        setOpenDialog(true);
    };


    const handleOpenReportDialog = (record: LitigationDeadline | null = null, editing: boolean = false) => {
        setCurrentRecord(record || {
            litigationDeadlineRecordId: 0,
            name: '',
            cpfId: 0,
            referredToAGODateString: '',
            agoLetterSentDateString: '',
            deadlineForResponseAGOString: '',
            mailedToSheriffDateString: '',
            complaintFiledDateString: '',
            servedDateString: '',
            returnedBySheriffDateString: '',
            proofOfServiceDateString: '',
            notifiedSOOrClerkDateString: '',
            trackingOrderMailedToDDateString: '',
            trackingOrderMailedToCourtDateString: '',
            answerDueDateString: '',
            answerFiledDateString: '',
            mtnEnterDefaultFiledDateString: '',
            hearingDateString: '',
            defaultEnteredDateString: '',
            reportFiledDateString: '',
            notifiedSOOrClerkReportDateString: '',
            resolutionStatus: 0,
            isArchived: false,
            reportType: 0,
            reportYearDateString: '',
            isLocal: false,
            unfiledReports: []
        });
        setIsEditing(editing);
        setOpenReportDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setCurrentRecord(null);
        setErrors({
            name: { error: false, helperText: "" },
            cpfId: { error: false, helperText: "" },
        });
    };


    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentRecord(prev => prev ? { ...prev, [name]: value } : null);
    };

    const handleOpenUnfiledReportDialog = (report: UnfiledReport | null = null) => {
        setCurrentUnfiledReport(report || {
            unfiledReportId: 0,
            localFilerRecordId: currentRecord!.litigationDeadlineRecordId,
            reportTypeId: 0,
            reportTypeDescription: '',
            year: '',
            reportStatusId: 0,
            reportStatusDescription : ''
        });
        setOpenUnfiledReportDialog(true);
    };

    const handleCloseUnfiledReportDialog = () => {
        setOpenUnfiledReportDialog(false);
        setCurrentUnfiledReport(null);
    };


    const handleUnfiledReportInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setCurrentUnfiledReport(prev => ({ ...prev!, [name]: value }));
    };

    const handleSaveUnfiledReport = async () => {
        if (currentUnfiledReport && currentRecord) {
            try {
                var params = [
                    `unfiledReportId=${currentUnfiledReport.unfiledReportId}`,
                    `localFilerRecordId=${currentUnfiledReport.localFilerRecordId}`,
                    `reportTypeId=${currentUnfiledReport.reportTypeId}`,
                    `year=${currentUnfiledReport.year}`,
                    `reportTypeId=${currentUnfiledReport.reportStatusId}`
                ].join('&');
                const url = `/NonfilerTracker/SaveFilersUnfiledReport?${params}`;
                const method = 'POST';

                const response = await fetch(url, {
                    method: method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(currentUnfiledReport)
                });

                if (response.ok) {
                    /*issue here?*/
                    console.log(currentUnfiledReport);

                    // If the unfiled report already exists, update it. Otherwise, add it to the list.
                    const updatedUnfiledReports =
                        currentUnfiledReport.unfiledReportId ?
                            currentRecord.unfiledReports.map(report =>
                                report.unfiledReportId === currentUnfiledReport.unfiledReportId ? currentUnfiledReport : report
                            ) :
                            [...currentRecord.unfiledReports, currentUnfiledReport];



                    setCurrentRecord({ ...currentRecord, unfiledReports: updatedUnfiledReports });
                    ; handleCloseUnfiledReportDialog();

                } else {
                    console.error('Failed to save unfiled report');
                }
            } catch (error) {
                console.error('Error saving unfiled report:', error);
            }
        }
    };

    const handleDeleteUnfiledReport = async (unfiledReportId: number) => {
        if (currentRecord) {
            try {
                const response = await fetch(`/NonfilerTracker/DeleteFilersUnfiledReport?unfiledReportId=${unfiledReportId}`, {
                    method: 'DELETE'
                });

                if (response.ok) {
                    const updatedUnfiledReports = currentRecord.unfiledReports.filter(
                        report => report.unfiledReportId !== unfiledReportId
                    );
                    setCurrentRecord({ ...currentRecord, unfiledReports: updatedUnfiledReports });
                } else {
                    console.error('Failed to delete unfiled report');
                }
            } catch (error) {
                console.error('Error deleting unfiled report:', error);
            }
        }
    };

    const handleCloseReportDialog = async () => {
        getLitigationDeadlines();
        setOpenReportDialog(false);
    };


    // Validation function
    const validateFields = () => {
        let tempErrors = { ...errors }; // Clone the current errors state

        // Check for 'name' validation
        if (!currentRecord?.name) {
            tempErrors.name = {
                error: true,
                helperText: "Name is required",
            };
        } else {
            tempErrors.name = { error: false, helperText: "" };
        }

        // Check for 'cpfId' validation (if applicable)
        if (!filterCriteria.isLocal && !currentRecord?.cpfId) {
            tempErrors.cpfId = {
                error: true,
                helperText: "CPF ID is required",
            };
        } else {
            tempErrors.cpfId = { error: false, helperText: "" };
        }

        // Update the error state
        setErrors(tempErrors);

        // Return whether there are any errors
        return Object.values(tempErrors).some(field => field.error);
    };




    const handleSave = async () => {
        if (currentRecord && !validateFields()) {
            try {
                const params = [
                    `Id=${currentRecord.litigationDeadlineRecordId}`,
                    `Name=${currentRecord.name}`,
                    `CpfId=${currentRecord.cpfId}`,
                    `ReferredToAGODateString=${currentRecord.referredToAGODateString}`,
                    `AGOLetterSentDateString=${currentRecord.agoLetterSentDateString}`,
                    `DeadlineForReponseAGODateString=${currentRecord.deadlineForResponseAGOString}`,
                    `MailedToSheriffDateString=${currentRecord.mailedToSheriffDateString}`,
                    `ComplaintFiledDateString=${currentRecord.complaintFiledDateString}`,
                    `ServedDateString=${currentRecord.servedDateString}`,
                    `ReturnedBySheriffDateString=${currentRecord.returnedBySheriffDateString}`,
                    `ProofOfServiceDateString=${currentRecord.proofOfServiceDateString}`,
                    `NotifiedSOOrClerkDateString=${currentRecord.notifiedSOOrClerkDateString}`,
                    `TrackingOrderMailedToDDateString=${currentRecord.trackingOrderMailedToDDateString}`,
                    `TrackingOrderMailedToCourtDateString=${currentRecord.trackingOrderMailedToCourtDateString}`,
                    `AnswerDueDateString=${currentRecord.answerDueDateString}`,
                    `AnswerFiledDateString=${currentRecord.answerFiledDateString}`,
                    `MtnEnterDefaultFiledDateString=${currentRecord.mtnEnterDefaultFiledDateString}`,
                    `HearingDateString=${currentRecord.hearingDateString}`,
                    `DefaultEnteredDateString=${currentRecord.defaultEnteredDateString}`,
                    `ReportFiledDateString=${currentRecord.reportFiledDateString}`,
                    `NotifiedSOOrClerkReportDateString=${currentRecord.notifiedSOOrClerkReportDateString}`,
                    `ResolutionStatus=${currentRecord.resolutionStatus}`,
                    `IsArchived=${currentRecord.isArchived}`,
                    `ReportType=${currentRecord.reportType}`,
                    `ReportYearDateString=${currentRecord.reportYearDateString}`,
                    `IsLocal=${currentRecord.isLocal}`,
                ].join('&');
                const url = `/NonfilerTracker/SaveLitigationDeadlineRecord?${params}`;
                const method = 'POST';

                const response = await fetch(url, {
                    method: method,
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(currentRecord)
                });

                console.log(response);
      

                if (response.ok) {
                    getLitigationDeadlines();  // Re-fetch the data to refresh the table
                    handleCloseDialog();
                } else {
                    console.error('Failed to save record');
                }
            } catch (error) {
                console.error('Error saving record:', error);
            }
        }
    };

    const handleDelete = async (litigationDeadlineRecordId: number) => {
        /*    have to delete record and then each unfiled report associated with that record*/
        try {
            const response = await fetch(`/NonfilerTracker/DeleteLitigationDeadlineRecord?litigationDeadlineRecordId=${litigationDeadlineRecordId}`, {
                method: 'DELETE'
            });

            if (response.ok) {
                getLitigationDeadlines();
            } else {
                console.error('Failed to delete record');
            }
        } catch (error) {
            console.error('Error deleting record:', error);
        }

    };

    const handleArchive = async (litigationDeadlineRecordId: number) => {
        /*    have to delete record and then each unfiled report associated with that record*/
        try {
            const response = await fetch(`/NonfilerTracker/ArchiveLitigationDeadlineRecord?litigationDeadlineRecordId=${litigationDeadlineRecordId}`, {
                method: 'POST'
            });

            if (response.ok) {
                getLitigationDeadlines();
            } else {
                console.error('Failed to delete record');
            }
        } catch (error) {
            console.error('Error deleting record:', error);
        }

    };

    const handleExport = async () => {
        const params = new URLSearchParams({
            SortField: sortField,
            SortDirection: sortDirection,
            ReportYear: filterCriteria.year,
            ReportFiled: filterCriteria.reportFiled.toString(),
            IsArchived: filterCriteria.isArchived.toString(),
            isLocal: filterCriteria.isLocal.toString()
        });

        try {
            const url = `/NonfilerTracker/ExportToExcel?${params}`;
            const response = await fetch(url);

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            // Get the blob from the response
            const blob = await response.blob();

            // Create a URL for the blob
            const downloadUrl = window.URL.createObjectURL(blob);

            // Create a temporary anchor element and trigger the download
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = 'exported_data.xlsx'; // You can set a default filename here
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            window.URL.revokeObjectURL(downloadUrl);


        } catch (error) {
            console.error('Error exporting:', error);
            alert("An error occurred while exporting the data. Please try again.");
        }
    }



    return (
        <div>
            <FilterLitigationDeadlines
                handleFilter={handleFilter}
            />
            <Box display="flex" justifyContent="flex-end" width="100%" p={2}>
                <Link
                    component="button"
                    variant="body1"
                    onClick={handleExport}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    <CloudDownload sx={{ mr: 1, fontSize: 'inherit' }} />
                    Export to Excel
                </Link>
            </Box>

            <LitigationDeadlineTable
                litigationDeadlinesList={litigationDeadlinesList}
                handleOpenDialog={handleOpenDialog}
                handleArchive={handleArchive}
                handleSort={handleSort}
                sortField={sortField}
                sortDirection={sortDirection}
                filterCriteria={filterCriteria}
                handleOpenReportDialog={handleOpenReportDialog}
            />

            <Button onClick={() => handleOpenDialog()} variant="contained" color="primary"
                sx={{
                    margin: 0,
                    top: 'auto',
                    right: 20,
                    bottom: 20,
                    left: 'auto',
                    position: 'fixed'
                }}>Add New AGO Referral</Button>



            <EditOrAddNewLitigationDeadline
                currentRecord={currentRecord}
                isEditing={isEditing}
                openDialog={openDialog}
                handleCloseDialog={handleCloseDialog}
                handleInputChange={handleInputChange}
                handleSave={handleSave}
                filterCriteria={filterCriteria}
                errors={errors}
            />

            <EditOrAddNewUnfiledReport
                currentUnfiledReport={currentUnfiledReport}
                currentRecord={currentRecord}
                openUnfiledReportDialog={openUnfiledReportDialog}
                isEditing={isEditing}
                isLitigationTab={true}
                openDialog={openReportDialog}
                handleCloseDialog={handleCloseReportDialog}
                handleOpenUnfiledReportDialog={handleOpenUnfiledReportDialog}
                handleDeleteUnfiledReport={handleDeleteUnfiledReport}
                handleCloseUnfiledReportDialog={handleCloseUnfiledReportDialog}
                handleUnfiledReportInputChange={handleUnfiledReportInputChange}
                handleSaveUnfiledReport={handleSaveUnfiledReport}
            />

        </div>
    );
}

export default LitigationDeadlines;